import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

// Theme: 'system' (follow prefers-color-scheme), or an explicit 'light' | 'dark' stamped on
// <html data-theme>. Persisted per browser; tokens.css does the rest.

export type ThemePref = 'system' | 'light' | 'dark';
const KEY = 'hf.theme';

interface ThemeCtx {
  pref: ThemePref;
  /** The resolved theme actually in effect. */
  resolved: 'light' | 'dark';
  setPref: (p: ThemePref) => void;
  toggle: () => void;
}

const Ctx = createContext<ThemeCtx | null>(null);

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* storage unavailable */
  }
  return 'system';
}

function systemDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function applyTheme(pref: ThemePref) {
  const root = document.documentElement;
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(readPref);
  const [sys, setSys] = useState(systemDark);

  useEffect(() => {
    applyTheme(pref);
    try {
      localStorage.setItem(KEY, pref);
    } catch {
      /* ignore */
    }
  }, [pref]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => setSys(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);

  const resolved: 'light' | 'dark' = pref === 'system' ? (sys ? 'dark' : 'light') : pref;
  const setPref = useCallback((p: ThemePref) => setPrefState(p), []);
  const toggle = useCallback(() => setPrefState(resolved === 'dark' ? 'light' : 'dark'), [resolved]);
  const value = useMemo(() => ({ pref, resolved, setPref, toggle }), [pref, resolved, setPref, toggle]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useTheme requires <ThemeProvider>');
  return c;
}
